import { html, View } from 'rune-ts';
import { ProductCardViewData } from '../../../../features/ProductList/type';
import klass from './ProductBadgeList.module.scss';
import { ProductBadgeBase } from '../ProductBadge/ProductBadgeBase';
import { ProductBadgeListController } from '@services/MarppleShop/renderApp/components/cells/ProductBadgeList/utils/ProductBadgeListController.ts';
import { ProductBadgeStrategyFactory } from '@services/MarppleShop/renderApp/components/cells/ProductBadgeList/strategies/product-badge-strategy.factory.ts';
import { ProductBadgeVariant } from './strategies';
import { ContextPageType, PageContext } from './strategies/product-badge-strategy.interface';
import { ProductBadgeOption } from '@services/MarppleShop/features/ProductBadge/type';
import { hydrateSafetyDate } from '../../../../../../shared/utils/Front/hydration.ts';
import { TimerTick } from '@services/MarppleShop/renderApp/components/cells/ProductBadgeList/utils/timer.ts';
import {
  ITimerSubscribable,
  TimerSubscriptionManager,
} from '@services/MarppleShop/renderApp/components/cells/ProductBadgeList/utils/TimerSubscriptionManager.ts';

export type ProductBadgeListData = Pick<
  ProductCardViewData,
  | 'is_public'
  | 'ranking'
  | 'sell_start_at'
  | 'sell_end_at'
  | 'quantity'
  | 'order_count'
  | 'is_quantity_public'
  | 'out_of_stock'
  | 'is_token_gate'
  | 'ordered_at'
  | 'is_membership'
  | 'is_trending'
  | 'is_popular'
  | 'is_brand_deal'
  | 'is_funding'
> & {
  server_timestamp: number;
};

export type FundingBadgeListData = Pick<
  ProductCardViewData,
  | 'is_public'
  | 'quantity'
  | 'order_count'
  | 'is_quantity_public'
  | 'out_of_stock'
  | 'is_membership'
  | 'is_funding'
> & {
  sell_start_at: Date;
  sell_end_at: Date;
  server_timestamp: number;
};

export interface ProductBadgeListOption extends ProductBadgeOption {
  /**
   * @description 표시할 최대 뱃지 수
   * @default Infinity
   */
  max_length?: number;

  /**
   * @deprecated styles 프로퍼티를 사용해주세요.
   */
  klass?: string;

  styles?: {
    container?: string;
  };
}

export class ProductBadgeList extends View<ProductBadgeListData> implements ITimerSubscribable {
  private _badgeViews: ProductBadgeBase[];

  private readonly timerSubscription: TimerSubscriptionManager;

  /**
   * static 함수로 생성해주세요.
   *
   * @example
   * ```ts
   * ProductBadgeList.forList(ProductBadgeVariant.DEFAULT_BADGE, data, option);
   * ```
   */
  private constructor(
    data: ProductBadgeListData,
    private readonly option: Readonly<ProductBadgeListOption>,
    private readonly variant: ProductBadgeVariant,
    private readonly pageContext: PageContext,
  ) {
    super(hydrateSafetyDate(data, ['sell_start_at', 'sell_end_at']), option, variant, pageContext);

    const strategy = ProductBadgeStrategyFactory.create(variant);
    const controller = new ProductBadgeListController(this.data, option, strategy, pageContext);
    this._badgeViews = controller.createProductBadges();

    this.timerSubscription = new TimerSubscriptionManager(this);
  }

  get badgeViews() {
    return this._badgeViews;
  }

  /**
   * 상품 목록에서 사용
   */
  static forList(
    variant: ProductBadgeVariant,
    data: Readonly<ProductBadgeListData>,
    option: Readonly<ProductBadgeListOption>,
  ): ProductBadgeList {
    return new ProductBadgeList(data, option, variant, {
      pageType: ContextPageType.LIST,
    });
  }

  /**
   * 상품 상세에서 사용
   */
  static forDetail(
    variant: ProductBadgeVariant,
    data: Readonly<ProductBadgeListData>,
    option: Readonly<ProductBadgeListOption>,
  ): ProductBadgeList {
    return new ProductBadgeList(data, option, variant, {
      pageType: ContextPageType.DETAIL,
    });
  }

  protected override onRender(): void {
    super.onRender();

    if (!this.timerSubscription.isSubscribed) {
      this.timerSubscription.subscribe();
    }
  }

  protected override onUnmount(): void {
    super.onUnmount();
    this.destroy();
  }

  private getHiddenKlass(): string {
    return this.badgeViews.length ? '' : klass.hidden;
  }

  onTimerTick(event: TimerTick): void {
    const strategy = ProductBadgeStrategyFactory.create(this.variant);
    const controller = new ProductBadgeListController(
      {
        ...this.data,
        server_timestamp: event.detail.timestamp,
      },
      this.option,
      strategy,
      this.pageContext,
    );

    this._badgeViews = controller.createProductBadges();
    this.redraw();
  }

  destroy(): void {
    this.timerSubscription.unsubscribe();
  }

  protected override template() {
    return html`<div
      class="${this.option.styles?.container ?? ''} ${klass.badge_container} ${this.getHiddenKlass()}"
    >
      ${this.badgeViews}
    </div>`;
  }
}
